import React, { type PropsWithChildren, useCallback, useMemo } from 'react';

import { getTypeIcon } from '@atlassian/people-teams-ui';

import { prefixWithContextEntryPoint } from '../../../common/utils/url';
import { useNavigationState } from '../store/main';
import {
	type AppConfigProviderProps,
	type AppConfigProviderType,
	type Flag,
	type FlagProps,
} from '../types';

const error = new Error('AppConfig is not setup in RSS');

const defaultValue = {
	push: () => {
		throw error;
	},
	replace: () => {
		throw error;
	},
	addFlag: () => {
		throw error;
	},
	removeFlag: () => {},
};

const AppConfigContext = React.createContext<AppConfigProviderType>(defaultValue);

export function AppConfigProvider({
	children,
	push: pushProp,
	replace: replaceProp,
	addFlag: addFlagProp,
	removeFlag,
}: PropsWithChildren<AppConfigProviderProps>): React.JSX.Element {
	const { contextEntryPoint } = useNavigationState();

	const addFlag = useCallback(
		(props: Flag) => {
			const { type, ...rest } = props;

			addFlagProp({
				...rest,
				id: rest.id ? `${rest.id}.${Date.now()}` : Date.now(),
				type,
				appearance: 'normal',
				icon: getTypeIcon(type),
			});
		},
		[addFlagProp],
	);

	const push = useCallback(
		(path: string) => {
			const finalPath = prefixWithContextEntryPoint(path, contextEntryPoint);
			return pushProp(finalPath);
		},
		[pushProp, contextEntryPoint],
	);

	const replace = useCallback(
		(path: string) => {
			const finalPath = prefixWithContextEntryPoint(path, contextEntryPoint);

			return replaceProp(finalPath);
		},
		[contextEntryPoint, replaceProp],
	);

	const configValue = useMemo(
		() => ({
			addFlag,
			removeFlag: removeFlag || (() => {}),
			push,
			replace,
		}),
		[addFlag, removeFlag, push, replace],
	);

	return <AppConfigContext.Provider value={configValue}>{children}</AppConfigContext.Provider>;
}

const useAppConfigProvider = () => React.useContext(AppConfigContext);

export const useNavigationActions = (): {
	push: (url: string) => void;
	replace: (url: string) => void;
} => {
	const { push, replace } = useAppConfigProvider();
	return { push, replace };
};

export const useAddFlag = (): ((props: FlagProps) => void) => {
	const { addFlag } = useAppConfigProvider();
	return addFlag;
};

export const useRemoveFlag = (): ((id: number | string) => void) => {
	const { removeFlag } = useAppConfigProvider();
	return removeFlag;
};
